import * as React from "react";
import { ArrowState, SliderMode } from "../components/Slider";

class SliderHelper {
	sliderRootRef: React.RefObject<HTMLDivElement>;
	cardWidth: number;
	cardGutter: number;
	sliderWidth: number;
	slider: HTMLElement;
	sliderViewport: HTMLElement;
	sliderViewportWidth: number;
	nextScrollCardCount: number;
	maxScroll: number;

	translateX: number;
	arrowState: ArrowState;
	sliderMode: SliderMode;

	constructor(ref: React.RefObject<HTMLDivElement>) {
		this.sliderRootRef = ref;
		this.nextScrollCardCount = 3;
		this.maxScroll = -1;
		this.translateX = 0;

		const card = this.sliderRootRef.current.querySelector(".csrec-card");
		if (card) {
			this.cardWidth = card.clientWidth;
			const marginLeftParsed = window
				.getComputedStyle(card)
				.marginRight.match(/(-?\d+)px$/);
			if (marginLeftParsed && marginLeftParsed.length === 2) {
				this.cardGutter = parseFloat(marginLeftParsed[1]) * 2;
			}
		}
		this.slider = this.sliderRootRef.current.querySelector(
			".csrec-slider-main"
		);
		this.sliderViewport = this.sliderRootRef.current.querySelector(
			".csrec-slider-viewport"
		);
		if (this.slider) {
			this.sliderWidth = this.slider.clientWidth;
		}
		this.updateViewportDimensions();
	}

	updateViewportDimensions() {
		this.sliderViewportWidth = this.calculateSliderViewportWidth();
	}

	updateSliderDimensions() {
		this.sliderWidth = this.slider.clientWidth;
	}

	calculateSliderViewportWidth(): number {
		const style = window.getComputedStyle(this.sliderViewport);
		const { width, paddingLeft, paddingRight } = style;
		return (
			this.getValueFromPixel(width) -
			this.getValueFromPixel(paddingLeft) -
			this.getValueFromPixel(paddingRight)
		);
	}

	getValueFromPixel(value: string): number {
		return parseInt(value.replace(/px$/, ""));
	}

	scrollRight(amount: number): ArrowState {
		return this.scroll(amount);
	}

	scrollLeft(amount: number): ArrowState {
		return this.scroll(-amount);
	}

	getScrollOffset(): number {
		return this.nextScrollCardCount * (this.cardWidth + this.cardGutter);
	}

	scroll(offset: number): ArrowState {
		this.setCurrentScroll();
		this.translateX += offset;
		let arrowState: ArrowState;
		if (this.translateX === 0) {
			arrowState = ArrowState.PeekRight;
		} else if (Math.abs(this.translateX) === this.getMaxScroll()) {
			arrowState = ArrowState.PeekLeft;
		} else {
			arrowState = ArrowState.PeekLeftAndRight;
		}

		return arrowState;
	}

	calculateArrowState(): ArrowState {
		return this.sliderMode === SliderMode.Scroll
			? this.calculateArrowStateOnScrollMode()
			: this.calculateArrowStateOnSliderMode();
	}

	calculateArrowStateOnSliderMode(): ArrowState {
		if (this.slider.clientWidth <= this.sliderViewport.clientWidth) {
			return ArrowState.NoArrow;
		}
		const maxScroll = this.getMaxScroll();
		const showLeftScroll = this.translateX < 0;
		const showRightScroll = -this.translateX < maxScroll;

		if (showLeftScroll && showRightScroll) {
			return ArrowState.PeekLeftAndRight;
		}

		if (showLeftScroll && !showRightScroll) {
			return ArrowState.PeekLeft;
		}

		if (!showLeftScroll && showRightScroll) {
			return ArrowState.PeekRight;
		}
	}

	calculateArrowStateOnScrollMode(): ArrowState {
		const target: HTMLElement = this.sliderViewport;
		if (target.scrollLeft === 0) {
			return ArrowState.PeekRight;
		} else if (
			target.scrollLeft >=
			this.sliderWidth - this.sliderViewportWidth
		) {
			return ArrowState.PeekLeft;
		} else {
			return ArrowState.PeekLeftAndRight;
		}
	}

	setArrowState(): void {
		this.arrowState = this.calculateArrowState();
	}

	calculateCurrentScroll(): number {
		let transformX: number = 0;
		const transform = this.slider.style.transform;
		const matches = transform.match(/translateX\((-?\d+)px\)/);
		if (matches && matches.length === 2) {
			transformX = parseFloat(matches[1]);
		}
		return transformX;
	}

	anchorSliderPositionToEndIfNeeded() {
		if (
			this.sliderMode !== SliderMode.Slider ||
			this.sliderWidth <= this.sliderViewportWidth
		) {
			return;
		}
		const dislocationFromRight =
			-this.translateX + this.sliderViewportWidth - this.sliderWidth;
		if (dislocationFromRight <= 0) {
			return;
		}
		this.translateX += dislocationFromRight;
	}

	setCurrentScroll(): void {
		this.translateX = this.calculateCurrentScroll();
	}

	getMaxScroll(): number {
		return this.sliderWidth - this.sliderViewportWidth;
	}

	moveLeft(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): ArrowState {
		const scrollRight = this.getScrollOffset();
		this.setCurrentScroll();
		const actualScroll = Math.min(scrollRight, -this.translateX);
		return this.scrollRight(actualScroll);
	}

	moveRight(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): ArrowState {
		const scrollLeft = this.getScrollOffset();
		this.setCurrentScroll();
		const actualScroll = Math.min(
			scrollLeft,
			this.getMaxScroll() + this.translateX
		);
		return this.scrollLeft(actualScroll);
	}

	resetScroll() {
		this.sliderViewport &&
			this.sliderViewport.scrollTo &&
			this.sliderViewport.scrollTo(0, 0);
	}

	calculateSliderMode() {
		return window.getComputedStyle(this.sliderViewport).overflowX === "hidden"
			? SliderMode.Slider
			: SliderMode.Scroll;
	}

	setSliderMode() {
		this.sliderMode = this.calculateSliderMode();
	}
}

export default SliderHelper;
